import { useRef } from "react";
import { useCancelBooking } from "api/api-hooks/use-cancel-booking";
import { useUpdateBooking } from "api/api-hooks/use-update-booking";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import { useBookingId } from "hooks/use-booking-id";
import { useBookingActionsContext, useBookingStateContext } from "hooks/use-booking-state";
import queryClient from "query-client";
import { useNavigate } from "react-router-dom";
import { checkIsDetailsForm, Routes } from "routes";
import { getSearchParams } from "search-params";
import { deriveUrlParamsFromState } from "search-params/url-params";
import { WebBooking } from "types/web-booking";

const useRemoveActivity = (
    beforeRemoveLastCallback?: () => void,
    onRemoveCompleteCallback?: () => void
) => {
    const { setState } = useBookingActionsContext();
    const state = useBookingStateContext();
    const { data: booking } = useWebBooking();
    const updateBooking = useUpdateBooking();
    const cancelBooking = useCancelBooking();
    const bookingId = useBookingId();
    const navigate = useNavigate();
    const { guests, guestsChildren, date } = state;
    const pendingRemovalSectionIds = useRef(new Set<number>());

    const applyOptimisticUpdate = () => {
        queryClient.setQueryData<WebBooking>(["booking", bookingId], (currentData) => {
            if (!currentData) return currentData;
            return new WebBooking({
                ...currentData,
                articles: currentData.articles.filter(
                    (a) => !pendingRemovalSectionIds.current.has(a.sectionId)
                ),
            });
        });
    };

    const removeActivities = async (activities: Array<{ ruleId: number; sectionId: number }>) => {
        const removedRuleIds = new Set(activities.map((a) => a.ruleId));

        const times = state.times
            ? state.times.filter((time) => time.ruleId === null || !removedRuleIds.has(time.ruleId))
            : [];
        const stateWithTimes = { ...state, times };
        setState?.(stateWithTimes);

        const removeTimes = activities
            .map((activity) => {
                const article = booking?.articles?.find(
                    (a) => a.ruleId === activity.ruleId && a.sectionId === activity.sectionId
                );
                if (!article?.start) return null;
                return { sectionId: activity.sectionId, startTime: article.start };
            })
            .filter((time): time is { sectionId: number; startTime: IMoment } => time !== null);

        if (removeTimes.length === 0) return;

        const allRuleIds = new Set(booking?.articles.map((item) => item.ruleId));
        const isLastItem =
            removedRuleIds.size === allRuleIds.size &&
            [...removedRuleIds].every((id) => allRuleIds.has(id));

        if (isLastItem) {
            if (beforeRemoveLastCallback) beforeRemoveLastCallback();
            cancelBooking.mutate({ systemTriggered: true });

            let newPath = location.pathname.replace(`/${booking?.guid}`, "");
            queryClient.removeQueries({ queryKey: ["booking", bookingId] });

            const { isExperienceViewMode } = getSearchParams();
            const isDetailsForm = checkIsDetailsForm(window.location.pathname);

            if (isDetailsForm) {
                // The request flow has no experience-view step: a request begun there leaves it.
                newPath = isExperienceViewMode
                    ? newPath
                          .replace(`/${Routes.BOOKING_REQUEST}`, "")
                          .replace(Routes.FINALIZE, Routes.EXPERIENCE_VIEW)
                    : newPath.replace(Routes.FINALIZE, Routes.AVAILABILITY);
            }

            const params = deriveUrlParamsFromState({ guests, guestsChildren, date });
            await navigate(
                { pathname: newPath, search: `?${params.toString()}` },
                { state: { intercept: false } }
            );
        } else {
            // Track all in-flight sectionIds before mutating. Multiple removeActivities calls
            // can run concurrently (mutations are serialised by scope, but the async functions
            // themselves overlap). After each mutation completes, useUpdateBooking refetches the
            // booking from the server — which only reflects removals up to that point and would
            // overwrite any optimistic updates for queued removals. By keeping a shared pending
            // set we can re-apply those optimistic removals immediately after each refetch.
            activities.forEach((a) => pendingRemovalSectionIds.current.add(a.sectionId));

            applyOptimisticUpdate();

            await updateBooking.mutateAsync({
                state: stateWithTimes,
                timesToUpdate: [],
                removeTimes,
            });

            activities.forEach((a) => pendingRemovalSectionIds.current.delete(a.sectionId));

            // The refetch inside updateBooking may have restored articles that belong to a
            // queued removal. Re-apply the optimistic update for any still-pending sectionIds.
            if (pendingRemovalSectionIds.current.size > 0) {
                applyOptimisticUpdate();
            }

            if (onRemoveCompleteCallback) onRemoveCompleteCallback();
        }
    };

    return {
        removeActivities,
    };
};

export { useRemoveActivity };
